import signupKeywordConfig from './reservedSignupKeywords.json';
import signupEmailConfig from './signupEmailValidationConfig.json';

export const SIGNUP_NAME_ERROR_RESERVED = 'signup-name-reserved';
export const SIGNUP_NAME_ERROR_RANDOM = 'signup-name-random';
export const SIGNUP_EMAIL_ERROR_SUSPICIOUS = 'signup-email-suspicious';

export const RESERVED_SIGNUP_KEYWORDS = signupKeywordConfig.reservedSignupKeywords;
export const FORBIDDEN_SIGNUP_NAME_PATTERNS = signupKeywordConfig.forbiddenNamePatterns;
export const BLOCKED_SIGNUP_EMAIL_DOMAINS = signupEmailConfig.blockedDomains;
export const SUSPICIOUS_EMAIL_PATTERN_DOMAINS = signupEmailConfig.suspiciousPatternDomains;

const suspiciousEmailPatternDomains = new Set(SUSPICIOUS_EMAIL_PATTERN_DOMAINS);

const normalize = v =>
  (v || '')
    .toString()
    .trim()
    .toLowerCase();

const nameTokens = value =>
  normalize(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const normalizeNameForSimilarity = value =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/[0]/g, 'o')
    .replace(/[1!|]/g, 'i')
    .replace(/[3]/g, 'e')
    .replace(/[5$]/g, 's')
    .replace(/[7]/g, 't')
    .replace(/[@]/g, 'a')
    .replace(/[^a-z]/g, '');

const isUpper = ch => ch >= 'A' && ch <= 'Z';

export const isReservedName = (value, reservedList = RESERVED_SIGNUP_KEYWORDS) => {
  const normalizedValue = normalize(value);
  if (!normalizedValue) {
    return false;
  }

  if (reservedList.includes(normalizedValue)) {
    return true;
  }

  const tokens = nameTokens(normalizedValue);
  return tokens.some(token => reservedList.includes(token));
};

export const looksRandomName = (value, options = {}) => {
  const { allowNumbers = false, minLength = 2, maxLength = 40 } = options;

  const raw = (value || '').toString();
  const trimmed = raw.trim();

  if (!trimmed) {
    return false;
  }

  const compact = trimmed.replace(/\s+/g, '');

  if (compact.length < minLength || compact.length > maxLength) {
    return true;
  }

  const digits = (compact.match(/\d/g) || []).length;
  if (!allowNumbers && digits > 0) {
    return true;
  }

  // Accept letters, apostrophes, hyphens, and spaces. Must start/end with a letter.
  if (!/^[A-Za-z][A-Za-z' -]*[A-Za-z]$/.test(trimmed)) {
    return true;
  }

  // Repeated single character bursts ("aaaa", "xxxxx")
  if (/(.)\1\1\1/.test(compact)) {
    return true;
  }

  // Name-like words should generally have vowels once the string is long enough.
  const vowels = (compact.match(/[aeiou]/gi) || []).length;
  if (compact.length >= 5 && vowels === 0) {
    return true;
  }

  if (/[bcdfghjklmnpqrstvwxyz]{6,}/i.test(compact)) {
    return true;
  }

  const hasLower = /[a-z]/.test(compact);
  const hasUpper = /[A-Z]/.test(compact);
  if (hasLower && hasUpper && compact.length >= 8) {
    const switches = compact
      .split('')
      .slice(1)
      .reduce(
        (count, char, index) => count + (isUpper(char) !== isUpper(compact[index]) ? 1 : 0),
        0
      );

    if (switches >= Math.floor(compact.length * 0.7)) {
      return true;
    }
  }

  return false;
};

export const validateHumanName = (value, config = {}) => {
  const {
    reservedMessage = 'This name is not allowed.',
    randomMessage = 'Please enter a real name.',
    reservedList = RESERVED_SIGNUP_KEYWORDS,
    allowNumbers = false,
    minLength = 2,
    maxLength = 40,
  } = config;

  const trimmedValue = (value || '').toString().trim();
  if (!trimmedValue) {
    return undefined;
  }

  if (isReservedName(trimmedValue, reservedList)) {
    return reservedMessage;
  }

  if (
    looksRandomName(trimmedValue, {
      allowNumbers,
      minLength,
      maxLength,
    })
  ) {
    return randomMessage;
  }

  return undefined;
};

export const levenshteinDistance = (source = '', target = '') => {
  if (!source.length) {
    return target.length;
  }
  if (!target.length) {
    return source.length;
  }

  const matrix = [];
  for (let row = 0; row <= target.length; row += 1) {
    matrix[row] = [row];
    for (let column = 1; column <= source.length; column += 1) {
      matrix[row][column] =
        row === 0
          ? column
          : Math.min(
              matrix[row - 1][column] + 1,
              matrix[row][column - 1] + 1,
              matrix[row - 1][column - 1] + (source[column - 1] === target[row - 1] ? 0 : 1)
            );
    }
  }

  return matrix[target.length][source.length];
};

export const collapseRepeats = value => value.replace(/(.)\1+/g, '$1');

export const forbiddenNameValidator = (
  forbiddenNames = FORBIDDEN_SIGNUP_NAME_PATTERNS,
  reservedMessage
) => {
  return value => {
    if (!value) {
      return undefined;
    }

    const cleanedValue = normalizeNameForSimilarity(value);
    const normalizedValue = collapseRepeats(cleanedValue);

    for (const forbiddenName of forbiddenNames) {
      if (normalizedValue.includes(forbiddenName)) {
        return reservedMessage;
      }

      const distance = levenshteinDistance(normalizedValue, forbiddenName);
      if (distance <= 1) {
        return reservedMessage;
      }
    }

    return undefined;
  };
};

const parseEmailParts = value => {
  const normalized = normalize(value);
  const parts = normalized.split('@');
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return null;
  }

  return {
    localPart: parts[0].split('+')[0],
    domain: parts[1],
  };
};

const letterCount = value => (value.match(/[a-z]/g) || []).length;
const numericToken = value => /^\d+$/.test(value);
const shortAlphaToken = value => /^[a-z]{1,2}$/.test(value);
const shortAlphaNumericToken = value =>
  value.length <= 3 && (/^[a-z]\d+[a-z]?$/.test(value) || /^\d+[a-z]$/.test(value));

export const isBlockedSignupEmailDomain = value => {
  const emailParts = parseEmailParts(value);
  if (!emailParts) {
    return false;
  }

  return BLOCKED_SIGNUP_EMAIL_DOMAINS.some(
    blockedDomain =>
      emailParts.domain === blockedDomain || emailParts.domain.endsWith(`.${blockedDomain}`)
  );
};

export const isSuspiciousSignupEmail = value => {
  const emailParts = parseEmailParts(value);
  if (!emailParts) {
    return false;
  }

  if (isBlockedSignupEmailDomain(value)) {
    return true;
  }

  if (!suspiciousEmailPatternDomains.has(emailParts.domain)) {
    return false;
  }

  const tokens = emailParts.localPart.split('.').filter(Boolean);
  if (tokens.length < 2) {
    return false;
  }

  const firstToken = tokens[0];
  const tailTokens = tokens.slice(1);
  const hasNameLikePrefix = /^[a-z][a-z0-9]{5,}$/.test(firstToken) && letterCount(firstToken) >= 6;
  if (!hasNameLikePrefix) {
    return false;
  }

  const numericTailCount = tailTokens.filter(numericToken).length;
  const shortAlphaTailCount = tailTokens.filter(shortAlphaToken).length;
  const alphaNumericTailCount = tailTokens.filter(shortAlphaNumericToken).length;
  const lastToken = tailTokens[tailTokens.length - 1] || '';

  if (tokens.length === 2 && numericToken(lastToken) && lastToken.length >= 3) {
    return true;
  }

  if (tokens.length >= 3 && alphaNumericTailCount >= 1 && numericTailCount >= 1) {
    return true;
  }

  if (tokens.length >= 4 && numericTailCount >= 2) {
    return true;
  }

  return (
    tokens.length >= 4 &&
    shortAlphaTailCount >= 2 &&
    numericToken(lastToken) &&
    lastToken.length >= 3
  );
};

export const validateSignupEmailField = email => {
  if (!email) {
    return null;
  }

  return isSuspiciousSignupEmail(email)
    ? {
        field: 'email',
        code: SIGNUP_EMAIL_ERROR_SUSPICIOUS,
      }
    : null;
};

export const suspiciousEmailValidator = errorMessage => value => {
  return isSuspiciousSignupEmail(value) ? errorMessage : undefined;
};

const getSignupNameValidationCode = (
  value,
  {
    reservedList = RESERVED_SIGNUP_KEYWORDS,
    forbiddenNames = FORBIDDEN_SIGNUP_NAME_PATTERNS,
    allowNumbers = false,
    minLength = 2,
    maxLength = 40,
  } = {}
) => {
  const trimmedValue = (value || '').toString().trim();
  if (!trimmedValue) {
    return null;
  }

  if (isReservedName(trimmedValue, reservedList)) {
    return SIGNUP_NAME_ERROR_RESERVED;
  }

  const similarityError = forbiddenNameValidator(forbiddenNames, 'reserved')(trimmedValue);
  if (similarityError) {
    return SIGNUP_NAME_ERROR_RESERVED;
  }

  if (
    looksRandomName(trimmedValue, {
      allowNumbers,
      minLength,
      maxLength,
    })
  ) {
    return SIGNUP_NAME_ERROR_RANDOM;
  }

  return null;
};

export const validateSignupNameFields = (nameValues = {}, options = {}) => {
  const { firstName, lastName } = nameValues;

  const firstNameCode = getSignupNameValidationCode(firstName, options);
  if (firstNameCode) {
    return {
      field: 'firstName',
      code: firstNameCode,
    };
  }

  const lastNameCode = getSignupNameValidationCode(lastName, options);
  if (lastNameCode) {
    return {
      field: 'lastName',
      code: lastNameCode,
    };
  }

  return null;
};

export const createSignupNameValidationApiError = validationIssue => {
  if (!validationIssue || !validationIssue.code) {
    return null;
  }

  const { field, code } = validationIssue;
  const detail =
    code === SIGNUP_NAME_ERROR_RESERVED
      ? 'Reserved or disallowed keyword is not allowed in signup name fields.'
      : code === SIGNUP_EMAIL_ERROR_SUSPICIOUS
      ? 'Please use a valid personal or business email address.'
      : 'Signup name looks invalid or random.';
  const title =
    code === SIGNUP_EMAIL_ERROR_SUSPICIOUS ? 'Invalid signup email' : 'Invalid signup name';

  return {
    name: 'SignupValidationError',
    message: 'Signup validation failed.',
    status: 400,
    statusText: 'Bad Request',
    data: {
      errors: [
        {
          status: 400,
          code,
          title,
          detail,
          ...(field ? { source: { pointer: `/data/attributes/${field}` } } : {}),
        },
      ],
    },
  };
};
